import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Button, Stack, Autocomplete, IconButton, Tooltip, Typography, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent } from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { styled } from '@mui/material/styles';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import WarningIcon from '@mui/icons-material/Warning';
import { useHelpContent } from '../../utils/HelpUtils';
import { hasDuplicateContent, getDuplicateContentMessage } from '../../utils/CategoryUtils';
import { Category } from '../../types/Category';

// Add type definitions for Web Speech API
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionError) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionError extends Event {
  error: string;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

// Extend Window interface to include webkitSpeechRecognition
declare global {
  interface Window {
    webkitSpeechRecognition: {
      new (): SpeechRecognition;
    };
  }
}

interface ContentEditorProps {
  subcases: string[];
  initialSubcase?: string;
  initialText?: string;
  category?: Category | null;
  contentId?: string | undefined;
  onSave: (subcase: string, text: string) => void;
  onCancel: () => void;
  highlightedText?: string;
  searchTerms?: string[];
  searchMode?: 'any' | 'all' | 'exact' | 'semantic';
  wholeWord?: boolean;
  caseSensitive?: boolean;
  showWarning?: boolean;
}

const EditorContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px',
  overflow: 'auto',
  height: '100%',
});

const TopBar = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
});

const EditorWrapper = styled(Box)({
  flex: 1,
  minHeight: 0,
  '& .quill': {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  '& .ql-container': {
    flex: 1,
    overflow: 'auto',
  },
  '& .ql-editor': {
    minHeight: '200px',
  },
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const LanguageSelect = styled(FormControl)(({ theme }) => ({
  minWidth: 120,
  marginRight: theme.spacing(2),
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  marginTop: theme.spacing(1),
  marginLeft: theme.spacing(1),
}));

const WarningBanner = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.warning.light,
  color: theme.palette.warning.contrastText,
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  border: `1px solid ${theme.palette.warning.main}`,
}));

// Custom styles for highlighting
const highlightStyles = `
  .ql-editor .ql-background {
    background-color: yellow !important;
  }
  .ql-editor .ql-color {
    color: black !important;
  }
  .ql-editor [style*="background: yellow"] {
    background-color: yellow !important;
    color: black !important;
  }
  .ql-editor [style*="background-color: yellow"] {
    background-color: yellow !important;
    color: black !important;
  }
`;

const ContentEditor: React.FC<ContentEditorProps> = ({
  subcases,
  initialSubcase = '',
  initialText = '',
  category,
  contentId,
  onSave,
  onCancel,
  highlightedText,
  searchTerms,
  searchMode = 'exact',
  wholeWord = false,
  caseSensitive = false,
  showWarning = false,
}) => {
  
  const [subcase, setSubcase] = useState(initialSubcase);
  const [text, setText] = useState(initialText);
  const [error, setError] = useState('');
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    // Load default language from localStorage, fallback to 'en-US'
    const saved = localStorage.getItem('defaultSpeechLanguage');
    return saved || 'en-US';
  });
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const quillRef = useRef<any>(null);
  const { getToolbarTooltipTextForState } = useHelpContent();

  const languages = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'he-IL', name: 'Hebrew' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'pt-BR', name: 'Portuguese' },
    { code: 'ru-RU', name: 'Russian' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'zh-CN', name: 'Chinese' },
  ];

  const appendTranscriptToQuill = (transcript: string) => {
    const cleaned = (transcript || '').trim();
    if (!cleaned) return;

    const quill = quillRef.current?.getEditor?.();
    if (!quill) {
      // Fallback: best-effort append to the current HTML string
      setText(prev => prev + (prev ? ' ' : '') + cleaned);
      return;
    }

    const range = quill.getSelection(true);
    const insertAt = range ? range.index : quill.getLength();
    const prefix = insertAt > 0 ? quill.getText(insertAt - 1, 1) : '';
    const needsSpace = prefix && !/\s/.test(prefix) && !/^\s/.test(cleaned);

    quill.insertText(insertAt, `${needsSpace ? ' ' : ''}${cleaned}`, 'user');
    quill.setSelection(insertAt + (needsSpace ? 1 : 0) + cleaned.length, 0, 'silent');
    setText(quill.root.innerHTML);
  };

  // Keep editor state in sync with props (separate from speech recognition lifecycle)
  useEffect(() => {
    console.log('ContentEditor useEffect - initialText:', initialText);
    setSubcase(initialSubcase);
    setText(initialText);
  }, [initialSubcase, initialText]);

  // Initialize speech recognition (avoid tying this to `initialText`, which may change during editing)
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window)) {
      recognitionRef.current = null;
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = selectedLanguage;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const results = event.results;

      let interim = '';
      let finalText = '';

      // Many Chrome builds provide multiple results; commit all finalized segments from the event.
      for (let i = (event as any).resultIndex ?? 0; i < results.length; i++) {
        const r = results[i];
        const t = r?.[0]?.transcript || '';
        if (!t) continue;
        if (r.isFinal) finalText += t + ' ';
        else interim += t + ' ';
      }

      const cleanedFinal = finalText.trim();
      if (cleanedFinal) {
        appendTranscriptToQuill(cleanedFinal);
      }
    };

    recognition.onerror = (event: SpeechRecognitionError) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognitionRef.current = recognition;

    return () => {
      try {
        recognition.stop();
      } catch (_) {
        // ignore
      }
    };
  }, [selectedLanguage]);

  // Effect to handle editor focus/blur for tooltip control
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const editorElement = editor.root;
      
      const handleFocus = () => {
        setIsEditing(true);
      };
      const handleBlur = () => {
        setIsEditing(false);
      };
      
      editorElement.addEventListener('focus', handleFocus);
      editorElement.addEventListener('blur', handleBlur);
      
      return () => {
        editorElement.removeEventListener('focus', handleFocus);
        editorElement.removeEventListener('blur', handleBlur);
      };
    }
  }, [quillRef.current]);

  // Effect to handle text highlighting
  useEffect(() => {

    if (highlightedText && highlightedText.trim() && searchTerms && searchTerms.length > 0) {
      // Add a longer delay to ensure the editor is fully loaded and text is set
      const timer = setTimeout(() => {
        if (quillRef.current) {
          const quill = quillRef.current.getEditor();

          if (quill) {

          }
        }
        highlightTextInEditor(searchTerms || [], searchMode, wholeWord, caseSensitive);
      }, 500); // Increased delay to ensure editor is ready
      return () => clearTimeout(timer);
    } else {

      // Don't clear highlights when there's no highlightedText to avoid clearing content
    }
  }, [highlightedText, initialText, searchTerms, searchMode, wholeWord, caseSensitive]);

  const highlightTextInEditor = (searchTerms: string[], searchMode: 'any' | 'all' | 'exact' | 'semantic', wholeWord: boolean, caseSensitive: boolean) => {
    
    if (!quillRef.current) {
      return;
    }

    const quill = quillRef.current.getEditor();
    if (!quill) {
      return;
    }

    // Clear existing highlights first
    clearHighlights();

    // Get the text content from Quill
    const text = quill.getText();
    console.log('Editor text:', text);
    
    // Determine which terms to highlight based on search mode
    let termsToHighlight: string[] = [];
    
    if (searchMode === 'exact') {
      // For exact mode, highlight the entire search string
      termsToHighlight = [searchTerms.join(' ')];
    } else {
      // For 'any' and 'all' modes, highlight individual terms
      termsToHighlight = searchTerms;
    }

    
    // Find all occurrences of each term and highlight them
    const allMatches: Array<{index: number, length: number}> = [];
    
    termsToHighlight.forEach(term => {
      
      if (wholeWord) {
        // Unicode-aware word boundary
        const escapedPattern = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const wordBoundary = new RegExp(`(?<=^|[^\\p{L}\\p{N}])${escapedPattern}(?=[^\\p{L}\\p{N}]|$)`, caseSensitive ? 'gu' : 'giu');
        let match;
        let matchCount = 0;
        const maxMatches = 100; // Safety limit
        while ((match = wordBoundary.exec(text)) !== null && matchCount < maxMatches) {
          allMatches.push({
            index: match.index,
            length: match[0].length
          });
          matchCount++;
        }
        if (matchCount >= maxMatches) {
          console.warn('Reached maximum match limit for term:', term);
        }

      } else {
        // For partial matches, use simple regex
        const searchRegex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseSensitive ? 'g' : 'gi');
        
        let match;
        let matchCount = 0;
        const maxMatches = 100; // Safety limit
        
        while ((match = searchRegex.exec(text)) !== null && matchCount < maxMatches) {
          
          allMatches.push({
            index: match.index,
            length: match[0].length
          });
          matchCount++;
        }
        
        if (matchCount >= maxMatches) {
          console.warn('Reached maximum match limit for term:', term);
        }
      }
    });


    // Sort matches by index to avoid conflicts when applying highlights
    allMatches.sort((a, b) => a.index - b.index);

    // Apply highlighting to each match using Quill's formatText
    allMatches.forEach(({index, length}) => {
      try {
        console.log(`Attempting to highlight text at index ${index}, length ${length}`);
        quill.formatText(index, length, {
          'background': 'yellow'
        });
        console.log(`Successfully highlighted text at index ${index}, length ${length}`);
      } catch (error) {
        console.error('Error highlighting text:', error);
      }
    });

    console.log('Highlights applied');
  };

  const clearHighlights = () => {
    if (!quillRef.current) return;

    const quill = quillRef.current.getEditor();
    if (!quill) return;

    // Get the text length
    const textLength = quill.getText().length;
    
    // Only remove background color formatting, not the text itself
    if (textLength > 0) {
      try {
        // Remove only background color formatting
        quill.formatText(0, textLength, {
          'background': false
        });
      } catch (error) {
        console.error('Error clearing highlights:', error);
      }
    }
  };

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
    // Save the selected language as default
    localStorage.setItem('defaultSpeechLanguage', newLanguage);
    if (recognitionRef.current) {
      recognitionRef.current.lang = newLanguage;
    }
  };

  const handleTextChange = (content: string) => {
    setText(content);
    if (error && !error.includes('already exists')) {
        setError('');
      }
    setSaveAttempted(false);
  };

  const handleTextFocus = () => {
    setIsEditing(true);
  };

  const handleTextBlur = () => {
    setIsEditing(false);
  };

  const handleSubcaseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSubcase(event.target.value);
    if (saveAttempted && error && !error.includes('already exists')) {
        setError('');
      }
  };

  const handleSave = () => {
    setSaveAttempted(true);
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
    if (!subcase.trim()) {
      setError('Subcase is required');
      return;
    }
    // Check if the text is empty by removing HTML tags and whitespace
    const textWithoutHtml = text.replace(/<[^>]*>/g, '').trim();
    if (!textWithoutHtml) {
      setError('Content is required');
      return;
    }

    // Strip HTML tags and save the clean text
    const cleanText = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    // Check for duplicate content
    if (category && hasDuplicateContent(category, subcase, cleanText, contentId)) {
      setError(getDuplicateContentMessage(subcase));
      setSaveAttempted(true);
      return;
    }

    onSave(subcase, cleanText);
  };

  const handleCancel = () => {
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
    setSaveAttempted(false);
    setError('');
    onCancel();
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      console.error('Speech recognition not supported');
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
    }
    setIsRecording(!isRecording);
  };

  return (
    <EditorContainer>
      <style>{highlightStyles}</style>
      {showWarning && (
        <WarningBanner>
          <WarningIcon />
          <Typography variant="body2">
            You have unsaved changes. Please save or cancel before navigating away.
          </Typography>
        </WarningBanner>
      )}
       {saveAttempted && error && text.replace(/<[^>]*>/g, '').trim() && subcase.trim() && (
            <WarningBanner sx={{ backgroundColor: 'error.light', color: 'error.contrastText', border: '1px solid error.main' }}>
              <WarningIcon />
              <Typography variant="body2">
                {error}
              </Typography>
            </WarningBanner>
          )}
      <TopBar>
        <Tooltip title={getToolbarTooltipTextForState('content-subcase', true)} placement="top">
          <Autocomplete
            freeSolo
            options={subcases}
            value={subcase}
            onChange={(_, newValue) => {
              setSubcase(newValue || '');
               if (error && !error.includes('already exists')) {
                 setError('');
               }
            }}
            onInputChange={(_, newInputValue) => {
              setSubcase(newInputValue);
              if (error && !error.includes('already exists')) {
                 setError('');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sub Case"
                variant="outlined"
                size="small"
                error={!!error && !subcase.trim() && saveAttempted}
                helperText={!!error && !subcase.trim() && saveAttempted ? error : ''}
              />
            )}
            size="small"
            sx={{ flex: 1, marginRight: 2 }}
          />
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('content-language', true)} placement="top">
          <LanguageSelect size="small">
            <InputLabel>Language</InputLabel>
            <Select
              value={selectedLanguage}
              onChange={handleLanguageChange}
              label="Language"
            >
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </LanguageSelect>
        </Tooltip>
        <Stack direction="row" spacing={1}>
          <>
            <Tooltip title={getToolbarTooltipTextForState(isRecording ? 'speech-recording-stop' : 'speech-recording', true)} placement="top">
              <span>
                <StyledIconButton
                  onClick={toggleRecording}
                  sx={{
                    backgroundColor: isRecording ? 'error.main' : 'transparent',
                    color: isRecording ? 'error.contrastText' : 'inherit',
                    '&:hover': {
                      backgroundColor: isRecording ? 'error.dark' : 'action.hover',
                    },
                    // Add pulsing animation when recording
                    animation: isRecording ? 'pulse 1.5s ease-in-out infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': {
                        boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.7)',
                      },
                      '70%': {
                        boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)',
                      },
                      '100%': {
                        boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)',
                      },
                    },
                  }}
                >
                  <MicIcon />
                </StyledIconButton>
              </span>
            </Tooltip>
            <Tooltip title={getToolbarTooltipTextForState('content-save', true)} placement="top">
              <span>
                <Button variant="contained" onClick={handleSave}>
                  Save
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={getToolbarTooltipTextForState('content-cancel', true)} placement="top">
              <span>
                <Button variant="outlined" onClick={handleCancel}>
                  Cancel
                </Button>
              </span>
            </Tooltip>
          </>
        </Stack>
      </TopBar>
      <Tooltip title={getToolbarTooltipTextForState('content-text-area', true)} placement="top">
        <EditorWrapper>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              '& .ql-editor': {
                fontSize: 'var(--app-font-size, 14px)',
                flex: 1,
                minHeight: '200px',
              },
              '& .ql-toolbar': {
                fontSize: 'var(--app-font-size, 14px)',
              },
              '& .ql-container': {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            <ReactQuill
              value={text}
              onChange={handleTextChange}
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'color': [] }, { 'background': [] }],
                  [{ 'align': [] }],
                  ['link', 'clean']
                ]
              }}
              formats={[
                'header',
                'bold', 'italic', 'underline', 'strike',
                'list', 'bullet',
                'color', 'background',
                'align',
                'link'
              ]}
              style={{
                border: saveAttempted && !text.replace(/<[^>]*>/g, '').trim() ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
              }}
              ref={quillRef}
            />
          </Box>
          {saveAttempted && !text.replace(/<[^>]*>/g, '').trim() && (
            <ErrorText variant="caption">Content is required</ErrorText>
          )}
        </EditorWrapper>
      </Tooltip>
    </EditorContainer>
  );
};

export default ContentEditor;